/**
 * @fileoverview Notification System for Multi-Channel User Communications
 * 
 * This module provides a comprehensive notification system that handles both in-app
 * and email notifications while respecting user preferences. It supports individual
 * and organization-wide notifications with granular preference-based control.
 * 
 * @module lib/notifications
 * 
 * Key Features:
 * - In-app notification creation with read/unread tracking
 * - Email queueing with immediate and scheduled sending
 * - User preference checking for 20+ notification types
 * - Organization-wide broadcast notifications
 * - Integration with email queue system
 * 
 * Common Use Cases:
 * 1. Job lifecycle events (RFQ opened, bid submitted, job awarded, PO issued)
 * 2. Milestone approvals and rejections
 * 3. Review reminders and verification updates
 * 4. Shipment tracking and delivery updates
 * 5. System announcements and organization invites
 * 
 * @example Basic notification
 * ```typescript
 * await createNotification({
 *   userId: 'user-123',
 *   organizationId: 'org-456',
 *   title: 'Job Awarded',
 *   message: 'Your bid has been selected!',
 *   type: 'success',
 *   category: 'bid',
 *   actionUrl: '/jobs/job-123',
 *   preferenceType: 'bid_updates'
 * });
 * ```
 */

import { supabase } from '@/integrations/supabase/client';

/**
 * Notification types for visual styling in UI
 */
export type NotificationType = 'info' | 'success' | 'warning' | 'error';

/**
 * Notification categories for grouping related notifications
 */
export type NotificationCategory = 'rfq' | 'bid' | 'job' | 'milestone' | 'review' | 'general';

/**
 * Granular notification preference types for user control
 * 
 * Users can separately enable/disable in-app and email notifications for each type.
 */
export type NotificationPreferenceType = 
  | 'rfq_matches'
  | 'bid_updates' 
  | 'review_reminders'
  | 'system_updates'
  | 'milestone_reminders'
  | 'file_uploads'
  | 'verification_updates'
  | 'organization_invites'
  | 'po_issued'
  | 'rfq_questions'
  | 'po_adjustments'
  | 'shipment_updates'
  | 'po_cancellations'
  | 'ach_payments';

/**
 * Parameters for creating a new notification
 */
interface CreateNotificationParams {
  userId: string;
  organizationId: string;
  title: string;
  message: string;
  type?: NotificationType;
  category?: NotificationCategory;
  relatedEntityType?: string;
  relatedEntityId?: string;
  actionUrl?: string;
  sendEmail?: boolean;
  emailTemplateName?: string;
  emailTemplateData?: Record<string, any>;
  preferenceType?: NotificationPreferenceType;
  sendImmediately?: boolean;
}

/**
 * Queues an email notification for sending
 * 
 * @internal
 */
const queueEmail = async (
  recipientEmail: string,
  templateName: string,
  templateData: Record<string, any> = {},
  sendImmediately: boolean = true
) => {
  try {
    const { data, error } = await supabase
      .from('email_queue')
      .insert({
        recipient_email: recipientEmail,
        template_name: templateName,
        template_data: templateData,
        status: 'queued',
        scheduled_for: new Date().toISOString(),
        send_immediately: sendImmediately
      })
      .select();

    if (error) {
      console.error('Failed to queue email:', error);
      return;
    }

    if (sendImmediately && data && data.length > 0) {
      try {
        // Dynamic import to avoid circular dependency
        const { invokeEdgeFunction } = await import('@/lib/edgeFunctionClient');
        await invokeEdgeFunction('email-notifications', {
          body: { immediate_email_id: data[0].id },
          callerOperation: 'notifications.sendImmediate',
        });
      } catch (err) {
        console.error('Error triggering immediate send:', err);
      }
    }
  } catch (error) {
    console.error('Error queuing email:', error);
  }
};

/**
 * Retrieves a user's email address by user ID
 * Uses a database function to look up emails for cross-user notifications
 * 
 * @internal
 */
const getUserEmail = async (userId: string): Promise<string | null> => {
  try {
    // Use RPC to get email from auth.users (works for any user, not just current)
    const { data, error } = await supabase.rpc('get_user_email_by_id', {
      target_user_id: userId
    });

    if (!error && data) {
      return data;
    }

    // Fallback: check if it's the current user
    const { data: { user } } = await supabase.auth.getUser();
    if (user && user.id === userId) {
      return user.email || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user email:', error);
    return null;
  }
};

/**
 * Checks if user has opted out of a notification preference type
 * 
 * @internal
 */
const checkUserPreferences = async (
  userId: string,
  preferenceType: NotificationPreferenceType
): Promise<{ allowInApp: boolean; allowEmail: boolean }> => {
  try {
    const { data, error } = await supabase
      .from('user_notification_preferences')
      .select(`inapp_${preferenceType}, email_${preferenceType}`)
      .eq('user_id', userId)
      .single();

    if (error || !data) {
      return { allowInApp: true, allowEmail: true };
    }

    return {
      allowInApp: data[`inapp_${preferenceType}`] ?? true,
      allowEmail: data[`email_${preferenceType}`] ?? true,
    };
  } catch (error) {
    console.error('Error checking user preferences:', error);
    return { allowInApp: true, allowEmail: true };
  }
};

/**
 * Creates a notification for a user with optional email delivery
 * 
 * This function handles the complete notification lifecycle:
 * 1. Checks user preferences if preferenceType is provided
 * 2. Creates in-app notification if user hasn't opted out
 * 3. Queues email if requested and user hasn't opted out
 */
export const createNotification = async (params: CreateNotificationParams) => {
  try {
    let allowInApp = true;
    let allowEmail = true;

    if (params.preferenceType) {
      const preferences = await checkUserPreferences(params.userId, params.preferenceType);
      allowInApp = preferences.allowInApp;
      allowEmail = preferences.allowEmail;
    }

    if (allowInApp) {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: params.userId,
          organization_id: params.organizationId,
          title: params.title,
          message: params.message,
          type: params.type || 'info',
          category: params.category || 'general',
          related_entity_type: params.relatedEntityType,
          related_entity_id: params.relatedEntityId,
          action_url: params.actionUrl,
          is_read: false
        });

      if (error) {
        console.error('Failed to create notification:', error);
      }
    }

    if (allowEmail && params.sendEmail && params.emailTemplateName) {
      const userEmail = await getUserEmail(params.userId);
      if (userEmail) {
        await queueEmail(
          userEmail,
          params.emailTemplateName,
          {
            title: params.title,
            message: params.message,
            actionUrl: params.actionUrl,
            ...params.emailTemplateData
          },
          params.sendImmediately !== false  // Default to true unless explicitly set to false
        );
      }
    }
  } catch (error) {
    console.error('Error creating notification:', error);
  }
};

/**
 * Sends a notification to all members of an organization
 */
export const notifyOrganization = async (
  organizationId: string,
  notification: Omit<CreateNotificationParams, 'userId' | 'organizationId'>
) => {
  try {
    const { data: members, error: membersError } = await supabase
      .from('profiles')
      .select('user_id')
      .eq('organization_id', organizationId);

    if (membersError) throw membersError;

    for (const member of members || []) {
      await createNotification({
        userId: member.user_id,
        organizationId,
        ...notification
      });
    }
  } catch (error) {
    console.error('Error notifying organization:', error);
  }
};

/**
 * Retrieves the vendor user ID associated with a job's winning bid
 */
export const getVendorUserFromJob = async (jobId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('awards')
      .select(`
        winning_bid_id,
        bids!inner(vendor_user_id)
      `)
      .eq('job_id', jobId)
      .single();

    if (error || !data) return null;
    return (data.bids as any)?.vendor_user_id || null;
  } catch (error) {
    console.error('Error getting vendor user from job:', error);
    return null;
  }
};

/**
 * Retrieves the organization ID for a given user
 */
export const getOrganizationIdFromUser = async (userId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('user_id', userId)
      .single();

    if (error || !data) return null;
    return data.organization_id;
  } catch (error) {
    console.error('Error getting organization from user:', error);
    return null;
  }
};
